import { uploadFileWeb } from "helpers/shared.helpers";
import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import theme from "shared/assets/theme";
import { DEPENDENCY_TYPE_ID_NAME_MAP } from "shared/constants";
import OrganizationService from "shared/services/Organization.service";
import CustomModal from "webComponentsFormElements/CustomModal.component";
import CustomButton from "webComponentsFormElements/inputs/CustomButton.component";
import SearchBar from "webComponentsFormElements/inputs/SearchBar.component";
import PlatformCard from "webComponentsScreens/platforms/PlatformCard.component";
import PlatformEditModalForm from "webComponentsScreens/platforms/PlatformEditModalForm.component";
import Banner from "webComponentsShared/Banner.component";
import { AuthContext } from "webContext/Auth.context";
import { ConfirmationContext } from "webContext/Confirmation.context";
import { OrganizationContext } from "webContext/Organization.context";
import { ToastContext } from "webContext/Toast.context";

const classes = StyleSheet.create({
  root: {
    flex: 1,
  },
  banner: {
    flexDirection: "row",
    gap: 20,
    alignItems: "center",
  },
  scrollContainer: {
    flex: 1,
  },
  container: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
    padding: 10,
  },
  noResultsText: {
    color: theme.color.darkGrey,
    marginTop: 20,
  },
});

export default function PlatformsScreen({ route, navigation }) {
  const { showToast } = useContext(ToastContext);
  const { showConfirmation } = useContext(ConfirmationContext);
  const { isMessagingManager } = useContext(AuthContext);

  const {
    organizationSystems,
    getOrganizationSystems,
    deleteOrganizationSystem,
    organizationSystemDependenciesMap,
    getOrganizationSystemDependencies,
    getTeams,
    getLocations,
    getDefaultPlatformFiles,
    getOtherContacts,
    teamList,
    locationList,
  } = useContext(OrganizationContext);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [openPlatform, setOpenPlatform] = useState({});
  const [openPlatformImageBlob, setOpenPlatformImageBlob] = useState(null);
  const [openPlatformImage, setOpenPlatformImage] = useState(null);
  const [oldOpenPlatformDependents, setOldOpenPlatformDependents] = useState(
    [],
  );
  const [openPlatformDependents, setOpenPlatformDependents] = useState([]);
  const [selectedOtherContactIds, setSelectedOtherContactIds] = useState([]);
  const [canSave, setCanSave] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const effectiveCanSave = canSave && (openPlatform.title?.length > 0);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await getOrganizationSystems();
      await getOrganizationSystemDependencies();
      await getTeams();
      await getLocations();
      await getDefaultPlatformFiles();
      await getOtherContacts();
      setIsLoading(false);
    };
    fetchData();
  }, [
    getOrganizationSystems,
    getOrganizationSystemDependencies,
    getTeams,
    getLocations,
    getDefaultPlatformFiles,
    getOtherContacts,
  ]);

  const handleSystemUpsert = async () => {
    try {
      let fileId;

      if (openPlatformImageBlob) {
        const res = await uploadFileWeb(openPlatformImageBlob);
        fileId = res.fileId;
      } else if (openPlatformImage?.fileId !== openPlatform.fileId) {
        fileId = openPlatformImage?.fileId ?? null;
      } else {
        fileId = openPlatform.fileId;
      }

      const systemData = { ...openPlatform, fileId };

      const finalDependents = openPlatformDependents.map((dependent) => {
        const foundOldDependent = oldOpenPlatformDependents.find(
          (oldDependent) =>
            oldDependent.dependencyTypeId === dependent.dependencyTypeId &&
            oldDependent.dependencyId === dependent.dependencyId,
        );

        if (foundOldDependent) {
          delete foundOldDependent._associations;
        }

        return foundOldDependent ?? dependent;
      });

      await OrganizationService.upsertOrganizationSystem(
        systemData,
        finalDependents,
        selectedOtherContactIds,
      );

      await getOrganizationSystems();
      await getOrganizationSystemDependencies();

      showToast(`Successfully saved ${openPlatform.title}.`, "success");
    } catch (err) {
      showToast(`Failed to save ${openPlatform.title}.`, "error");
    }
  };

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setIsCreating(false);
    setOldOpenPlatformDependents([]);
    setOpenPlatformDependents([]);
    setSelectedOtherContactIds([]);
    setOpenPlatformImage({ fileId: null, signedUrl: null });
    setOpenPlatformImageBlob(null);
    setCanSave(false);
    navigation.setParams({ organizationSystemId: undefined });
  }, [
    navigation,
    setCanSave,
    setOpenPlatformImage,
    setOpenPlatformImageBlob,
    setSelectedOtherContactIds,
    setOldOpenPlatformDependents,
    setOpenPlatformDependents,
    setIsCreating,
    setIsModalOpen,
  ]);

  const handleSystemDelete = useCallback(
    async (system) => {
      try {
        await deleteOrganizationSystem(system);
        closeModal();
        showToast(`Successfully deleted ${system.title}.`, "success");
      } catch {
        showToast(`Failed to delete ${system.title}.`, "error");
      }
    },
    [deleteOrganizationSystem, showToast, closeModal],
  );

  const onModalClose = useCallback(() => {
    if (effectiveCanSave) {
      showConfirmation({
        prompt: "You have unsaved changes. Are you sure you want to close?",
        onConfirm: closeModal,
      });
    } else {
      closeModal();
    }
  }, [effectiveCanSave, showConfirmation, closeModal]);

  const onModalSave = async () => {
    await handleSystemUpsert();

    closeModal();
  };

  const onPlatformEdit = useCallback(
    (platform) => {
      setIsCreating(false);
      setOpenPlatform(platform);
      const imageToSet =
        platform.fileId && platform.signedFileUrl
          ? { fileId: platform.fileId, signedUrl: platform.signedFileUrl }
          : null;
      setOpenPlatformImage(imageToSet);
      setOpenPlatformImageBlob(null);

      const dependents =
        platform._associations.organizationSystemDependencies?.map(
          (dependency) =>
            organizationSystemDependenciesMap.get(
              dependency.organizationSystemDependencyId,
            ),
        );
      setOldOpenPlatformDependents(dependents);
      setOpenPlatformDependents(dependents);

      const existingOtherContacts =
        platform._associations?.otherContacts?.map(
          (contact) => contact.otherContactId,
        ) || [];
      setSelectedOtherContactIds(existingOtherContacts);

      setIsModalOpen(true);
    },
    [organizationSystemDependenciesMap],
  );

  const getDeletePrompt = useCallback(
    (platform) => {
      const dependents =
        platform._associations.organizationSystemDependencies.map(
          (dependency) =>
            organizationSystemDependenciesMap.get(
              dependency.organizationSystemDependencyId,
            ),
        );
      const hasDependents = dependents.length > 0;
      const dependentsGrouped = hasDependents
        ? dependents.reduce(
            (acc, dependent) => {
              const type =
                DEPENDENCY_TYPE_ID_NAME_MAP[dependent.dependencyTypeId];
              if (type === "Team") {
                const team = teamList.find(
                  (aTeam) => aTeam.teamId === dependent.dependencyId,
                );
                if (team) {
                  acc.Teams.push({ name: team.name, type });
                }
              } else if (type === "Location") {
                const location = locationList.find(
                  (aLocation) =>
                    aLocation.locationId === dependent.dependencyId,
                );
                if (location) {
                  acc.Places.push({ name: location.name, type });
                }
              } else if (type === "Organization") {
                acc.Organization.push({ name: "Entire Organization", type });
              }
              return acc;
            },
            { Teams: [], Places: [], Organization: [] },
          )
        : { Teams: [], Places: [], Organization: [] };
      return hasDependents ? (
        <View>
          Are you sure you want to permanently delete the platform{" "}
          {platform.title}?<br />
          The following dependencies will also be removed:
          <br />
          <View style={{ marginTop: 10 }}>
            {Object.keys(dependentsGrouped).map(
              (key) =>
                dependentsGrouped[key].length > 0 && (
                  <View style={{ alignItems: "center" }} key={key}>
                    {key !== "Organization" && <Text>{key}</Text>}
                    <ul style={{ fontSize: 15, textAlign: "left" }}>
                      {dependentsGrouped[key].map((dependent, index) => (
                        <li key={dependent.name + index}>{dependent.name}</li>
                      ))}
                    </ul>
                  </View>
                ),
            )}
          </View>
        </View>
      ) : (
        `Are you sure you want to permanently delete the platform ${platform.title}?`
      );
    },
    [teamList, locationList, organizationSystemDependenciesMap],
  );

  const onPlatformDelete = useCallback(
    (platform) => {
      showConfirmation({
        prompt: getDeletePrompt(platform),
        onConfirm: () => handleSystemDelete(platform),
      });
    },
    [getDeletePrompt, handleSystemDelete, showConfirmation],
  );

  const onPlatformAdd = () => {
    setIsCreating(true);
    setOpenPlatform({ title: "", isOnline: true });
    setOpenPlatformImage({ fileId: null, signedUrl: null });
    setOpenPlatformImageBlob(null);
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (route.params?.organizationSystemId) {
      const platformToOpen = organizationSystems.find(
        (system) =>
          system.organizationSystemId === route.params.organizationSystemId,
      );
      if (platformToOpen) {
        setTimeout(() => onPlatformEdit(platformToOpen), 0);
      }
    }
  }, [route.params?.organizationSystemId, organizationSystems, onPlatformEdit]);

  const filteredPlatforms = organizationSystems.filter(
    (platform) =>
      platform.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      platform.description?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  if (isLoading) {
    return <ActivityIndicator size="large" />;
  }
  return (
    <>
      <CustomModal
        title={`${isCreating ? "Create" : "Edit"} Platform`}
        open={isModalOpen}
        canSave={effectiveCanSave}
        onClose={onModalClose}
        onSave={onModalSave}
        additionalButtons={{
          before: !isCreating && !isMessagingManager
            ? [
                {
                  title: "Delete Platform",
                  icon: "trash-can-outline",
                  variant: "danger",
                  onPress: () => onPlatformDelete(openPlatform),
                },
              ]
            : [],
        }}
        scroll
      >
        <PlatformEditModalForm
          platform={openPlatform}
          setPlatform={setOpenPlatform}
          dependents={oldOpenPlatformDependents}
          setPlatformDependents={setOpenPlatformDependents}
          image={openPlatformImage}
          setImage={setOpenPlatformImage}
          setImageBlob={setOpenPlatformImageBlob}
          selectedOtherContactIds={selectedOtherContactIds}
          setSelectedOtherContactIds={setSelectedOtherContactIds}
          setCanSave={setCanSave}
          canSave={effectiveCanSave}
          isCreating={isCreating}
        />
      </CustomModal>
      <View style={classes.root}>
        <Banner classes={classes.banner}>
          <SearchBar
            placeholder="Search platforms..."
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
          <CustomButton
            title="Add platform"
            icon="plus-circle-outline"
            variant="dark"
            onPress={onPlatformAdd}
          />
        </Banner>
        <ScrollView
          style={classes.scrollContainer}
          contentContainerStyle={{ paddingBottom: 20 }}
        >
          {filteredPlatforms.length > 0 ? (
            <View style={classes.container}>
              {filteredPlatforms.map((platform) => (
                <PlatformCard
                  platform={platform}
                  onPlatformEdit={onPlatformEdit}
                  key={platform.organizationSystemId}
                />
              ))}
            </View>
          ) : (
            <Text style={classes.noResultsText}>
              {searchQuery
                ? "No platforms match your search."
                : "No platforms found."}
            </Text>
          )}
        </ScrollView>
      </View>
    </>
  );
}
